// src/contexts/AuthContext.jsx
// PATCH (this version):
//   FIX SM-A1 (this version): clearPendingQueue() now called explicitly as a
//     safety-net in logout(), separate from clearSyncQueueOnSignOut().
//
//     Previously, clearPendingQueue() was only called INSIDE
//     clearSyncQueueOnSignOut(). If that function threw and the catch block
//     swallowed the error, clearPendingQueue() would be silently skipped —
//     leaving User A's unsent writes in the queue. When User B signed in and
//     triggered a flush, those records would be written to Firestore under
//     User B's auth token (partially mitigated by FIX #24's uid check, but
//     the queue would still be dirty).
//
//     Fix: clearPendingQueue() is now called in its own try/catch block after
//     clearSyncQueueOnSignOut() completes (or fails). This guarantees the
//     queue is cleared regardless of whether step 1 succeeded.
//
// ── PREVIOUS PATCH ──────────────────────────────────────────────────────────
//   FIX #6 (Flag 6, this version): clearCryptoSalt() is no longer called on
//     sign-out. The crypto salt (src/lib/crypto.js) is now keyed per-uid, so
//     different users on a shared device already get isolated salts without
//     needing a wipe on sign-out — and wiping it here would instead destroy
//     the signing-out user's OWN salt, making their PIN-encrypted data
//     undecryptable the next time they log back in.
//
//     Sign-out order (now):
//       1. clearSyncQueueOnSignOut() — stop flush + unsubscribe + clear queue
//       1b. clearPendingQueue()      — safety-net
//       2. clearAppData()            — wipe IDB user data
//       3. signOut(auth)             — clear Firebase token
// ─────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth }                        from '@/lib/firebase';
import { clearAppData, clearPendingQueue }  from '@/lib/db/storageAdapter';
import { flushPendingSyncWrites } from '@/hooks/sync/useSyncStore';
import { clearSyncQueueOnSignOut }     from '@/lib/sync/syncManager';

const AuthContext = createContext(null);

// ── Friendly error messages ───────────────────────────────────
function friendlyError(code) {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError,   setAuthError]   = useState(null);
  const [offlineMode, setOfflineMode] = useState(false);

  // ── Listen to Firebase auth state changes ─────────────────
  useEffect(() => {
    // If Firebase auth is unavailable (missing/invalid API key), go offline
    if (!auth) {
      console.warn('[AuthContext] Firebase auth unavailable — running in offline-only mode.');
      setOfflineMode(true);
      setUser({ uid: 'offline', displayName: 'Offline User', email: null });
      setAuthLoading(false);
      return;
    }
    let unsub;
    try {
      unsub = onAuthStateChanged(auth, (firebaseUser) => {
        setUser(firebaseUser);
        setAuthLoading(false);
      }, (error) => {
        console.error('[AuthContext] onAuthStateChanged error:', error);
        // Fall back to offline mode on auth error
        setOfflineMode(true);
        setUser({ uid: 'offline', displayName: 'Offline User', email: null });
        setAuthLoading(false);
      });
    } catch (err) {
      console.error('[AuthContext] Failed to set up auth listener:', err);
      setOfflineMode(true);
      setUser({ uid: 'offline', displayName: 'Offline User', email: null });
      setAuthLoading(false);
    }
    return () => unsub && unsub();
  }, []);

  // ── Login ──────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    if (offlineMode || !auth) return { success: false, error: 'Running in offline mode. Firebase is not configured.' };
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (err) {
      const msg = friendlyError(err.code);
      setAuthError(msg);
      return { success: false, error: msg };
    }
  }, [offlineMode]);

  // ── Register ───────────────────────────────────────────────
  const register = useCallback(async (email, password, name) => {
    if (offlineMode || !auth) return { success: false, error: 'Running in offline mode. Firebase is not configured.' };
    setAuthError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (name) {
        await updateProfile(result.user, { displayName: name });
      }
      return { success: true };
    } catch (err) {
      const msg = friendlyError(err.code);
      setAuthError(msg);
      return { success: false, error: msg };
    }
  }, [offlineMode]);

  // ── Logout ─────────────────────────────────────────────────
  // Sign-out checklist (order matters):
  //   1. clearSyncQueueOnSignOut() — stops background flush timer + interval,
  //      tears down all Firestore subscriptions, and calls clearPendingQueue()
  //      internally. This is the primary queue-clear path.
  //   1b. clearPendingQueue()      — FIX SM-A1: safety-net call in its own
  //      try/catch. If step 1 throws and is swallowed, this guarantees the
  //      queue is still wiped before the next user's session starts.
  //   2. clearAppData()            — wipes IDB appData store so User B
  //      doesn't briefly see User A's data on next login.
  //   3. signOut(auth)             — clears Firebase auth token, fires
  //      onAuthStateChanged(null) → AppContent re-renders → components
  //      unmount → Firestore onSnapshot listeners auto-unsubscribe.
  //
  //  FIX #6 (Flag 6): clearCryptoSalt() removed from this checklist — the
  //  crypto salt is now keyed per-uid (src/lib/crypto.js), so it no longer
  //  needs to be wiped on sign-out to isolate different users on a shared
  //  device, and wiping it here would have destroyed the signing-out user's
  //  own salt instead.
  //
  //  IDB / crypto failures are non-fatal — sign-out always completes.
  const logout = useCallback(async () => {
    setAuthError(null);

    try {
      // Persist debounced writes (e.g. the just-saved profile) while still signed in.
      await Promise.race([flushPendingSyncWrites(), new Promise((r) => setTimeout(r, 3000))]).catch(() => {});
      await clearSyncQueueOnSignOut();
      console.info('[AUTH] Sync queue cleared.');
    } catch (err) {
      console.error('[AUTH] clearSyncQueueOnSignOut failed (non-fatal):', err);
    }

    // FIX SM-A1: Safety-net — ensures the pending queue is empty even if
    // clearSyncQueueOnSignOut() threw above. clearSyncQueueOnSignOut() calls
    // clearPendingQueue() internally, but if it failed mid-way this guarantees
    // User A's queued writes cannot be flushed under User B's auth context.
    try {
      await clearPendingQueue();
    } catch (err) {
      console.error('[AUTH] clearPendingQueue safety-net failed (non-fatal):', err);
    }

    try {
      await clearAppData();
      console.info('[AUTH] IDB appData cleared.');
    } catch (err) {
      console.error('[AUTH] clearAppData failed (non-fatal):', err);
    }

    if (auth && !offlineMode) {
      try {
        await signOut(auth);
      } catch (err) {
        console.error('[AUTH] signOut error:', err);
      }
    }

    // Always explicitly clear user state — in offline mode, onAuthStateChanged
    // won't fire, so we must set user to null manually to show the login screen.
    setUser(null);
  }, [offlineMode]);

  const value = {
    user,
    isAuthenticated: !!user,
    authLoading,
    authError,
    setAuthError,
    login,
    register,
    logout,
    offlineMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}